/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
'use client';

import type { CollectionItem } from '@chakra-ui/react';
import { Select as ChakraSelect, Portal } from '@chakra-ui/react';
import * as React from 'react';

import { CloseButton } from './close-button';

interface SelectTriggerProps extends ChakraSelect.ControlProps {
	clearable?: boolean
}

export const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(function SelectTrigger(props, ref) {
	const { children, clearable, ...rest } = props;

	return (
		<ChakraSelect.Control {...rest}>
			<ChakraSelect.Trigger ref={ref}>
				{children}
			</ChakraSelect.Trigger>
			<ChakraSelect.IndicatorGroup>
				{clearable && <SelectClearTrigger />}
				<ChakraSelect.Indicator />
			</ChakraSelect.IndicatorGroup>
		</ChakraSelect.Control>
	);
});

const SelectClearTrigger = React.forwardRef<HTMLButtonElement, ChakraSelect.ClearTriggerProps>(function SelectClearTrigger(props, ref) {
	return (
		<ChakraSelect.ClearTrigger asChild {...props} ref={ref}>
			<CloseButton
				focusRingWidth="2px"
				focusVisibleRing="inside"
				pointerEvents="auto"
				size="xs"
				variant="plain"
			/>
		</ChakraSelect.ClearTrigger>
	);
});

interface SelectContentProps extends ChakraSelect.ContentProps {
	portalled?: boolean
	portalRef?: React.RefObject<HTMLElement>
}

export const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(function SelectContent(props, ref) {
	const { portalled = true, portalRef, ...rest } = props;

	return (
		<Portal container={portalRef} disabled={!portalled}>
			<ChakraSelect.Positioner>
				<ChakraSelect.Content {...rest} ref={ref} />
			</ChakraSelect.Positioner>
		</Portal>
	);
});

export const SelectItem = React.forwardRef<HTMLDivElement, ChakraSelect.ItemProps>(function SelectItem(props, ref) {
	const { children, item, ...rest } = props;

	return (
		<ChakraSelect.Item item={item} key={item.value} {...rest} ref={ref}>
			{children}
			<ChakraSelect.ItemIndicator />
		</ChakraSelect.Item>
	);
});

interface SelectValueTextProps extends Omit<ChakraSelect.ValueTextProps, 'children'> {
	children?(items: CollectionItem[]): React.ReactNode
}

export const SelectValueText = React.forwardRef<HTMLSpanElement, SelectValueTextProps>(function SelectValueText(props, ref) {
	// eslint-disable-next-line @typescript-eslint/unbound-method
	const { children, ...rest } = props;

	return (
		<ChakraSelect.ValueText {...rest} ref={ref}>
			<ChakraSelect.Context>
				{(select) => {
					const items = select.selectedItems;

					if (items.length === 0) return props.placeholder;

					if (children) return children(items);

					if (items.length === 1) return select.collection.stringifyItem(items[0]);

					return `${items.length} selected`;
				}}
			</ChakraSelect.Context>
		</ChakraSelect.ValueText>
	);
});

export const SelectRoot = React.forwardRef<HTMLDivElement, ChakraSelect.RootProps>(function SelectRoot(props, ref) {
	return (
		<ChakraSelect.Root
			{...props}
			positioning={{
				sameWidth: true,
				...props.positioning,
			}}
			ref={ref}
		>
			{props.asChild
				? (
						props.children
					)
				: (
						<>
							<ChakraSelect.HiddenSelect />
							{props.children}
						</>
					)}
		</ChakraSelect.Root>
	);
}) as ChakraSelect.RootComponent;

interface SelectItemGroupProps extends ChakraSelect.ItemGroupProps {
	label: React.ReactNode
}

export const SelectItemGroup = React.forwardRef<HTMLDivElement, SelectItemGroupProps>(function SelectItemGroup(props, ref) {
	const { children, label, ...rest } = props;

	return (
		<ChakraSelect.ItemGroup {...rest} ref={ref}>
			<ChakraSelect.ItemGroupLabel>
				{label}
			</ChakraSelect.ItemGroupLabel>
			{children}
		</ChakraSelect.ItemGroup>
	);
});

export const SelectLabel = ChakraSelect.Label;
export const SelectItemText = ChakraSelect.ItemText;
